import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { selectFieldClassName, selectCompactClassName } from './formStyles';
import { dropdownPanelVariantsForPlacement, dropdownTransition } from './dropdownMotion';
import {
  DROPDOWN_Z_INDEX,
  useFloatingDropdownPosition,
} from '../../hooks/useFloatingPanelPosition';

function parseOptionChildren(children) {
  return React.Children.toArray(children)
    .filter((child) => React.isValidElement(child) && child.type === 'option')
    .map((child) => ({
      value: child.props.value ?? '',
      label: child.props.children,
      disabled: Boolean(child.props.disabled),
    }));
}

function Select({
  variant = 'field',
  className = '',
  triggerClassName = '',
  labelClassName = '',
  panelClassName = '',
  centered = false,
  children,
  value,
  onChange,
  disabled,
  onFocus,
  'aria-label': ariaLabel,
  ...rest
}) {
  const options = useMemo(() => parseOptionChildren(children), [children]);
  const reducedMotion = useReducedMotion();
  const transition = dropdownTransition(reducedMotion);
  const listboxId = useId();

  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const panelPosition = useFloatingDropdownPosition({
    open,
    anchorRef: buttonRef,
    panelRef,
    itemCount: options.length,
  });

  const selectedOption =
    options.find((opt) => String(opt.value) === String(value)) ?? options[0];

  const enabledIndexes = useMemo(
    () => options.map((opt, idx) => (!opt.disabled ? idx : -1)).filter((idx) => idx >= 0),
    [options]
  );

  const baseTrigger = variant === 'compact' ? selectCompactClassName : selectFieldClassName;
  const triggerClasses =
    `${baseTrigger} select-ios-trigger ${className} ${triggerClassName}`.trim();

  const placement = panelPosition?.placement ?? 'bottom';
  const panelVariants = dropdownPanelVariantsForPlacement(placement);

  useEffect(() => {
    if (!open) {
      setHighlightIndex(-1);
      return undefined;
    }

    const selectedIdx = options.findIndex((opt) => String(opt.value) === String(value));
    const initial =
      selectedIdx >= 0 && !options[selectedIdx]?.disabled
        ? selectedIdx
        : enabledIndexes[0] ?? -1;
    setHighlightIndex(initial);

    function handlePointerDown(event) {
      const inButton = buttonRef.current?.contains(event.target);
      const inPanel = panelRef.current?.contains(event.target);
      if (!inButton && !inPanel) setOpen(false);
    }

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [open, options, value, enabledIndexes]);

  function handleChange(nextValue) {
    onChange?.({ target: { value: nextValue } });
    setOpen(false);
    buttonRef.current?.focus();
  }

  function moveHighlight(direction) {
    if (enabledIndexes.length === 0) return;
    const currentPos = enabledIndexes.indexOf(highlightIndex);
    let nextPos;
    if (currentPos < 0) {
      nextPos = direction > 0 ? 0 : enabledIndexes.length - 1;
    } else {
      nextPos = (currentPos + direction + enabledIndexes.length) % enabledIndexes.length;
    }
    setHighlightIndex(enabledIndexes[nextPos]);
  }

  function handleTriggerKeyDown(event) {
    if (disabled) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) setOpen(true);
        else moveHighlight(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!open) setOpen(true);
        else moveHighlight(-1);
        break;
      case 'Home':
        if (open && enabledIndexes.length) {
          event.preventDefault();
          setHighlightIndex(enabledIndexes[0]);
        }
        break;
      case 'End':
        if (open && enabledIndexes.length) {
          event.preventDefault();
          setHighlightIndex(enabledIndexes[enabledIndexes.length - 1]);
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!open) {
          setOpen(true);
        } else if (highlightIndex >= 0 && options[highlightIndex]) {
          handleChange(options[highlightIndex].value);
        }
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          setOpen(false);
        }
        break;
      default:
        break;
    }
  }

  function toggleOpen() {
    if (disabled) return;
    setOpen((current) => !current);
  }

  const activeDescendantId =
    highlightIndex >= 0 ? `${listboxId}-option-${highlightIndex}` : undefined;

  const dropdownPanel =
    open && panelPosition ? (
      <motion.ul
        ref={panelRef}
        id={listboxId}
        role="listbox"
        aria-label={ariaLabel}
        tabIndex={-1}
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={panelVariants}
        transition={transition}
        style={{
          position: 'fixed',
          top: panelPosition.top,
          left: panelPosition.left,
          width: panelPosition.width,
          maxHeight: panelPosition.maxHeight,
          zIndex: DROPDOWN_Z_INDEX,
        }}
        className={`dropdown-panel-ios overflow-auto p-1.5 ${
          placement === 'top' ? 'origin-bottom' : 'origin-top'
        } ${panelClassName}`.trim()}
      >
        {options.map((option, index) => {
          const selected = String(option.value) === String(value);
          const highlighted = index === highlightIndex;
          return (
            <motion.li
              key={String(option.value)}
              id={`${listboxId}-option-${index}`}
              role="option"
              aria-selected={selected}
              aria-disabled={option.disabled || undefined}
              initial={reducedMotion ? false : { opacity: 0, y: placement === 'top' ? -6 : 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={
                reducedMotion
                  ? { duration: 0.01 }
                  : { type: 'spring', stiffness: 420, damping: 32, delay: index * 0.035 }
              }
            >
              <button
                type="button"
                disabled={option.disabled}
                onClick={() => handleChange(option.value)}
                onMouseEnter={() => {
                  if (!option.disabled) setHighlightIndex(index);
                }}
                className={[
                  'dropdown-option-ios',
                  selected ? 'dropdown-option-ios-selected' : '',
                  highlighted && !selected ? 'bg-secondary-100/80 ' : '',
                  option.disabled ? 'pointer-events-none opacity-40' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span className="min-w-0 truncate">{option.label}</span>
                {selected ? (
                  <CheckIcon className="dropdown-option-check h-4 w-4 shrink-0" aria-hidden="true" />
                ) : null}
              </button>
            </motion.li>
          );
        })}
      </motion.ul>
    ) : null;

  return (
    <div className={`select-wrap ${variant === 'field' ? 'w-full' : ''} ${open ? 'select-wrap-open' : ''}`}>
      <button
        ref={buttonRef}
        type="button"
        role="combobox"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={open ? activeDescendantId : undefined}
        onClick={toggleOpen}
        onFocus={onFocus}
        onKeyDown={handleTriggerKeyDown}
        className={`${triggerClasses} ${variant === 'field' ? 'w-full' : ''} ${centered ? 'justify-center' : ''} ${open ? 'select-ios-trigger-open' : ''}`}
        {...rest}
      >
        <span
          className={`min-w-0 truncate ${centered ? 'text-center' : 'flex-1 text-left'} ${labelClassName}`.trim()}
        >
          {selectedOption?.label}
        </span>
        <ChevronDownIcon
          className={`select-chevron transition-transform duration-300 ease-in-out ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </button>

      {typeof document !== 'undefined'
        ? createPortal(<AnimatePresence>{dropdownPanel}</AnimatePresence>, document.body)
        : null}
    </div>
  );
}

export default Select;
